import {
    applyActiveRequestMode,
    setProjectionExecutors
} from './projections';

/**
 * Мутации для карточки заявки
 */
export default {
    setBuildingProcessDraft(state, option) {
        if (!state.request) return;
        state.request = {...state.request,
            building_process_id: option ? option.value : null,
            building_object_name: option ? (option.building_object_name || option.object_name || option.text) : null,
            building_contract_name: option ? option.contract_name || null : null};
    },
    acknowledgeRequestFields(state, {requestId, fields, response = {}}) {
        if (!state.request || String(state.request.id) !== String(requestId) || !state.originalRequest) return;
        const original = {...state.originalRequest};
        Object.keys(fields).forEach(key => {
            if (key === 'remove_file_ids') {
                original.files = (original.files || []).filter(file => !fields[key].includes(file.id));
                state.removedFileIds = state.removedFileIds.filter(id => !fields[key].includes(id));
            } else if (key === 'custom_fields') {
                original.workflow_data = {...original.workflow_data, custom_fields: JSON.parse(JSON.stringify(fields[key]))};
            } else {
                const value = Object.prototype.hasOwnProperty.call(response, key) ? response[key] : fields[key];
                original[key] = value;
                if (state.request[key] === fields[key]) state.request[key] = value;
            }
        });
        if (response.updatedAt) {
            original.updatedAt = response.updatedAt;
            state.request.updatedAt = response.updatedAt;
        }
        state.originalRequest = original;
    },
    acknowledgeRequestItem(state, {requestId, itemId, payload}) {
        if (!state.request || String(state.request.id) !== String(requestId) || !state.originalRequest) return;
        state.originalRequest.items = (state.originalRequest.items || []).map(item =>
            item.id === itemId ? {...item, ...payload} : item);
    },
    acknowledgeMaterials(state, {requestId, changes, results}) {
        if (!state.request || String(state.request.id) !== String(requestId)) return;
        const merge = items => {
            const deleted = changes.deleted.map(item => item.id);
            const updated = new Map(changes.updated.map(item => [item.id, item]));
            return (items || []).filter(item => !deleted.includes(item.id))
                .map(item => updated.has(item.id) ? {...item, ...updated.get(item.id)} : item)
                .concat(changes.added.map((item, index) => ({...item, id: results.added[index], item_type: 'material'})));
        };
        state.request.items = merge(state.request.items);
        state.originalRequest.items = merge(state.originalRequest.items);
        state.materialsChanges = {added: [], updated: [], deleted: []};
        state.isEditingMaterialsList = false;
        state.materialsSaveRevision = (state.materialsSaveRevision || 0) + 1;
    },
    mergeDeadlineUpdate(state, request) {
        const patch = {};
        for (const key of ['deadline', 'priority', 'urgency_comment', 'requires_chief_engineer', 'status', 'updatedAt']) {
            if (Object.prototype.hasOwnProperty.call(request, key)) patch[key] = request[key];
        }
        state.request = {...state.request, ...patch};
        if (state.originalRequest) state.originalRequest = {...state.originalRequest, ...patch};
    },
    mergeDeadlineAccess(state, access) {
        const patch = {};
        for (const key of ['isAdmin', 'currentUserRole', 'rolePermissions', 'adminPermissions',
            'roleAvailableActions', 'adminAvailableActions', 'roleActionHints', 'adminActionHints']) {
            patch[key] = access[key];
        }
        state.request = {...state.request, ...patch};
        if (state.originalRequest) state.originalRequest = {...state.originalRequest, ...patch};
        applyActiveRequestMode(state);
    },
    /**
     * Установить заявку
     */
    setRequest(state, request) {
        state.request = request;
        applyActiveRequestMode(state);
    },

    /**
     * Установить оригинальную заявку (для отслеживания изменений)
     */
    setOriginalRequest(state, request) {
        state.originalRequest = request;
    },

    /**
     * Обновить поле заявки
     */
    updateRequestField(state, { field, value }) {
        if (state.request && (Object.prototype.hasOwnProperty.call(state.request, field) || field === 'workflow_data')) {
            state.request[field] = value;
        }
    },

    /**
     * Установить активность заявки
     */
    setRequestActivity(state, activity) {
        state.requestActivity = activity;
    },

    /**
     * Установить доступные действия
     */
    setAvailableActions(state, actions) {
        state.availableActions = actions;
    },

    /**
     * Установить доступных исполнителей
     */
    setAvailableExecutors(state, executors) {
        setProjectionExecutors(state, executors);
    },

    /**
     * Включить/выключить админскую проекцию прав карточки
     */
    setAdminGodModeEnabled(state, enabled) {
        state.adminGodModeEnabled = enabled === true;
        applyActiveRequestMode(state);
    },

    /**
     * Установить выбранного исполнителя
     */
    setSelectedExecutorId(state, executorId) {
        state.selectedExecutorId = executorId;
    },

    /**
     * Установить флаг загрузки
     */
    setLoading(state, loading) {
        state.loading = loading;
    },

    /**
     * Установить флаг сохранения
     */
    setSavingRequest(state, saving) {
        state.savingRequest = saving;
    },

    setUploadingRequiredAttachment(state, uploading) {
        state.uploadingRequiredAttachment = uploading;
    },

    mergeUploadedRequestFiles(state, { requestId, files }) {
        if (!state.request || String(state.request.id) !== String(requestId)) return;
        const uploaded = Array.isArray(files) ? files : [];
        const merge = list => {
            const byId = new Map((list || []).map(file => [String(file.id), file]));
            uploaded.forEach(file => byId.set(String(file.id), file));
            return Array.from(byId.values());
        };
        state.request.files = merge(state.request.files)
            .filter(file => !state.removedFileIds.some(id => String(id) === String(file.id)));
        if (state.originalRequest) {
            state.originalRequest.files = merge(state.originalRequest.files);
        }
    },

    /**
     * Установить флаг отправки
     */
    setSubmittingRequest(state, submitting) {
        state.submittingRequest = submitting;
    },

    /**
     * Установить флаг согласования
     */
    setApprovingRequest(state, approving) {
        state.approvingRequest = approving;
    },

    /**
     * Установить флаг отклонения
     */
    setRejectingRequest(state, rejecting) {
        state.rejectingRequest = rejecting;
    },

    /**
     * Установить флаг взятия в работу
     */
    setExecutingRequest(state, executing) {
        state.executingRequest = executing;
    },

    /**
     * Установить флаг возврата
     */
    setReturningRequest(state, returning) {
        state.returningRequest = returning;
    },

    /**
     * Установить флаг завершения
     */
    setCompletingRequest(state, completing) {
        state.completingRequest = completing;
    },

    /**
     * Установить флаг частичного выполнения
     */
    setPartiallyCompletingRequest(state, partiallyCompleting) {
        state.partiallyCompletingRequest = partiallyCompleting;
    },

    /**
     * Установить флаг принятия
     */
    setAcceptingRequest(state, accepting) {
        state.acceptingRequest = accepting;
    },

    /**
     * Установить флаг частичного принятия
     */
    setAcceptingPartiallyRequest(state, acceptingPartially) {
        state.acceptingPartiallyRequest = acceptingPartially;
    },

    /**
     * Установить флаг отмены
     */
    setCancellingRequest(state, cancelling) {
        state.cancellingRequest = cancelling;
    },

    /**
     * Установить флаг изменения статуса
     */
    setChangingStatus(state, changing) {
        state.changingStatus = changing;
    },

    /**
     * Установить флаг назначения исполнителя
     */
    setAssigningExecutor(state, assigning) {
        state.assigningExecutor = assigning;
    },

    /**
     * Установить флаг скачивания материалов
     */
    setDownloadingMaterials(state, downloading) {
        state.downloadingMaterials = downloading;
    },

    /**
     * Добавить новые файлы для загрузки
     */
    addNewAttachments(state, files) {
        const toKey = file => `${file.name}|${file.size}|${file.lastModified}`;
        const existing = new Map(state.newAttachments.map(file => [toKey(file), file]));
        files.forEach(file => {
            const key = toKey(file);
            if (!existing.has(key)) {
                existing.set(key, file);
            }
        });
        state.newAttachments = Array.from(existing.values());
    },

    /**
     * Заменить новые файлы для загрузки
     */
    setNewAttachments(state, files) {
        const toKey = file => `${file.name}|${file.size}|${file.lastModified}`;
        const next = new Map();
        (Array.isArray(files) ? files : []).forEach(file => {
            if (!file) {
                return;
            }
            const key = toKey(file);
            if (!next.has(key)) {
                next.set(key, file);
            }
        });
        state.newAttachments = Array.from(next.values());
    },

    /**
     * Удалить новый файл
     */
    removeNewAttachment(state, index) {
        if (index >= 0 && index < state.newAttachments.length) {
            state.newAttachments.splice(index, 1);
        }
    },

    /**
     * Очистить новые файлы
     */
    clearNewAttachments(state) {
        state.newAttachments = [];
        state.attachmentsInputValue = null;
    },

    /**
     * Установить значение input файлов
     */
    setAttachmentsInputValue(state, value) {
        state.attachmentsInputValue = value;
    },

    /**
     * Удалить файл заявки
     */
    removeRequestFile(state, fileId) {
        if (state.request && Array.isArray(state.request.files)) {
            state.request.files = state.request.files.filter(file => file.id !== fileId);
        }
        if (!state.removedFileIds.includes(fileId)) {
            state.removedFileIds.push(fileId);
        }
    },

    /**
     * Очистить список удаленных файлов
     */
    clearRemovedFileIds(state) {
        state.removedFileIds = [];
    },

    /**
     * Установить изменения материалов
     */
    setMaterialsChanges(state, changes) {
        state.materialsChanges = {
            added: Array.isArray(changes.added) ? [...changes.added] : [],
            updated: Array.isArray(changes.updated) ? [...changes.updated] : [],
            deleted: Array.isArray(changes.deleted) ? [...changes.deleted] : []
        };
    },

    /**
     * Очистить изменения материалов
     */
    clearMaterialsChanges(state) {
        state.materialsChanges = {
            added: [],
            updated: [],
            deleted: []
        };
        state.isEditingMaterialsList = false;
    },

    /**
     * Установить флаг редактирования списка материалов
     */
    setEditingMaterialsList(state, editing) {
        state.isEditingMaterialsList = editing;
    },

    /**
     * Установить таймер автосохранения
     */
    setAutoSaveTimer(state, timer) {
        state.autoSaveTimer = timer;
    },

    /**
     * Установить время последнего автосохранения
     */
    setLastAutoSaveTime(state, time) {
        state.lastAutoSaveTime = time;
    },

    /**
     * Сбросить состояние модуля
     */
    resetState(state) {
        state.request = null;
        state.originalRequest = null;
        state.requestActivity = [];
        state.availableActions = [];
        state.availableExecutors = [];
        state.adminGodModeEnabled = false;
        state.loading = false;
        state.selectedExecutorId = null;
        state.newAttachments = [];
        state.attachmentsInputValue = null;
        state.removedFileIds = [];
        state.materialsChanges = {
            added: [],
            updated: [],
            deleted: []
        };
        state.isEditingMaterialsList = false;
        
        // Очистка флагов загрузки
        state.savingRequest = false;
        state.submittingRequest = false;
        state.approvingRequest = false;
        state.rejectingRequest = false;
        state.executingRequest = false;
        state.returningRequest = false;
        state.completingRequest = false;
        state.partiallyCompletingRequest = false;
        state.acceptingRequest = false;
        state.acceptingPartiallyRequest = false;
        state.cancellingRequest = false;
        state.changingStatus = false;
        state.assigningExecutor = false;
        state.downloadingMaterials = false;
        
        // Очистка таймера автосохранения
        if (state.autoSaveTimer) {
            clearTimeout(state.autoSaveTimer);
            state.autoSaveTimer = null;
        }
        state.lastAutoSaveTime = null;
    }
};
